import { Suspense, useRef, useState } from "react";
import { Button, Col, Form, Stack } from "react-bootstrap";
import { Await, useRouteLoaderData } from "react-router-dom";
import { Archive, ArchiveFill, CheckLg, PencilFill, TrashFill, XLg } from "react-bootstrap-icons";
import { axiosInstance } from "../auth/Auth";
import ConfirmPrompt from "./ConfirmPrompt";

export default function ContentGroupRow({ contentGroup, onDelete, onEdit, limitReached }) {
    const groupId = contentGroup.group_id;

    const { radios } = useRouteLoaderData("root");
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(contentGroup.display_name);
    const [showConfirm, setShowConfirm] = useState(false);

    const inputRef = useRef(null);

    async function handleEditContentGroup(event) {
        event.preventDefault();
        try {
            const response = await axiosInstance.put(`content_testing/content_groups/${groupId}?name=${name}`);
            onEdit(groupId, response.data);
            setEditing(false);
        } catch (error) {}
    }

    async function handleDeleteContentGroup() {
        try {
            await axiosInstance.delete(`content_testing/content_groups/${groupId}`);
            onDelete(groupId);
        } catch (error) {
            if (error.response?.status === 404) onDelete(groupId);
        }
    }

    async function handleToggleDisabled() {
        try {
            const modifiedContentGroup = { ...contentGroup, is_disabled: !contentGroup.is_disabled };
            await axiosInstance.patch(`content_testing/content_groups/${groupId}/${modifiedContentGroup.is_disabled}`);
            onEdit(groupId, modifiedContentGroup);
        } catch (error) {}
    }

    function handleEditPress() {
        setEditing(true);
        inputRef.current.focus();
    }

    function handleDiscard() {
        setEditing(false);
        setName(contentGroup.display_name);
    }

    function handleKeyPress(e) {
        if (e.key === "Escape") {
            handleDiscard();
        }
    }

    return (
        <Form onSubmit={handleEditContentGroup} className="row p-0 m-0 align-items-center">
            <ConfirmPrompt
                bodyText="Are you sure you want to delete content group?"
                cancelText="No, go back"
                handleConfirm={handleDeleteContentGroup}
                handleClose={() => setShowConfirm(false)}
                show={showConfirm}
            />
            <Col xs={12} sm={5}>
                <Suspense>
                    <Await resolve={radios}>
                        {resolvedRadios => <span>{resolvedRadios.data.find(x => x.radio_id === contentGroup.radio_id)?.radio_name}</span>}
                    </Await>
                </Suspense>
            </Col>
            <Col xs>
                <Stack direction="horizontal">
                    <Form.Control
                        ref={inputRef}
                        type="text"
                        size="sm"
                        className="py-0"
                        readOnly={!editing}
                        plaintext={!editing}
                        onKeyDown={handleKeyPress}
                        value={name}
                        onChange={e => setName(e.currentTarget.value)}
                        placeholder="Content group name"
                    />
                    {editing ? (
                        <>
                            <Button type="submit" variant="success" size="sm" className="p-1 d-flex">
                                <CheckLg height={20} width={20} />
                            </Button>
                            <Button variant="danger" size="sm" className="p-1 d-flex" onClick={handleDiscard}>
                                <XLg height={20} width={20} />
                            </Button>
                        </>
                    ) : (
                        <Button variant="ra-yellow" size="sm" className="p-1 d-flex" onClick={handleEditPress}>
                            <PencilFill height={20} width={20} />
                        </Button>
                    )}
                </Stack>
            </Col>
            <Col xs={"auto"}>
                <Stack direction="horizontal" gap={1}>
                    <Button
                        variant="ra-dark-blue-outline"
                        size="sm"
                        className=" p-1 d-flex"
                        active={contentGroup.is_disabled}
                        title={contentGroup.is_disabled ? "Unarchive" : "Archive"}
                        disabled={contentGroup.is_disabled && limitReached}
                        onClick={handleToggleDisabled}>
                        {contentGroup.is_disabled ? <ArchiveFill height={20} width={20} /> : <Archive height={20} width={20} />}
                    </Button>
                    <Button variant="danger" size="sm" className=" p-1 d-flex" title="Delete" onClick={() => setShowConfirm(true)}>
                        <TrashFill height={20} width={20} />
                    </Button>
                </Stack>
            </Col>
        </Form>
    );
}
